import React from 'react';
import { RenderRequest } from 'types/cx-editor';
import { Box, Typography, useTheme } from '@mui/material';

type VariantConfig = {
  templateKey?: string;
  hierarchy?: string;
  locale?: string;
  scenario?: string;
};

type PreviewFrameProps = {
  variantName?: string;
  variant?: VariantConfig | null;
  renderRequest: RenderRequest | null;
  previewMode: 'desktop' | 'mobile';
  channel?: string;
  isFullTemplateSelected?: boolean;
  renderedHtml?: string;
  renderedSubject?: string;
  loading?: boolean;
};

export const PreviewFrame: React.FC<PreviewFrameProps> = ({
  variantName,
  variant,
  renderRequest,
  previewMode,
  channel = 'Email',
  isFullTemplateSelected = false,
  renderedHtml,
  renderedSubject,
  loading = false,
}) => {
  const theme = useTheme();
  // Always call hooks in the same order; avoid early returns before hooks
  const effectiveTemplateKey = variant?.templateKey || renderRequest?.templateKey;
  const effectiveElementKey = renderRequest?.elementKey || null;

  // Use rendered subject from API only
  const subjectLine = renderedSubject || null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minWidth: 0 }}>
      {!renderRequest && (
        <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">Select a template to preview</Typography>
        </Box>
      )}
      {variantName && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 1,
            padding: '6px 12px',
            borderRadius: 1.5,
            border: 1,
            borderColor: 'divider',
            bgcolor: theme => theme.palette.mode === 'dark' 
              ? 'rgba(255, 255, 255, 0.05)' 
              : 'rgba(0, 0, 0, 0.02)',
            color: 'text.secondary',
            fontSize: '12px',
            fontWeight: 500
          }}
        >
          <Typography component="span" variant="caption">{variantName}:</Typography>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.hierarchy || 'Project'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.locale || 'en_US'}
          </Box>
          <Box
            component="span"
            sx={{
              padding: '2px 8px',
              borderRadius: 0.5,
              bgcolor: theme => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.1)'
                : 'rgba(0, 0, 0, 0.08)',
              fontSize: '11px'
            }}
          >
            {variant?.scenario || 'default'}
          </Box>
        </Box>
      )}
      <Box
        sx={{
          flex: 1,
          borderRadius: 2,
          bgcolor: theme => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.03)'
            : 'rgba(0, 0, 0, 0.02)',
          padding: 2,
          overflow: 'auto'
        }}
      >
        {loading && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              padding: 5,
              color: 'text.secondary'
            }}
          >
            <Typography variant="body2">Loading preview...</Typography>
          </Box>
        )}

        {!loading && channel === 'Email' && subjectLine && isFullTemplateSelected && (
          <Box
            sx={{
              marginBottom: 2,
              borderRadius: 2,
              boxShadow: theme => theme.palette.mode === 'dark'
                ? '0 1px 3px rgba(0,0,0,0.3)'
                : '0 1px 3px rgba(0,0,0,0.1)',
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider'
            }}
          >
            <Box sx={{ padding: 1.5 }}>
              <Typography
                variant="caption"
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  color: 'text.secondary',
                  display: 'block',
                  marginBottom: 0.5
                }}
              >
                Subject Line
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '14px', margin: 0 }}>
                {subjectLine}
              </Typography>
            </Box>
          </Box>
        )}

        {!loading && renderedHtml ? (
          // Render HTML from API - apply mobile or desktop styling based on previewMode
          previewMode === 'mobile' && channel !== 'HER' ? (
            // Mobile preview with phone frame
            <Box
              sx={{
                margin: '0 auto',
                width: '360px',
                height: '740px',
                bgcolor: '#000000', // Phone frame should remain black
                border: '14px solid #000000',
                borderRadius: '40px',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 20px 25px rgba(0,0,0,0.6)'
                  : '0 20px 25px rgba(0,0,0,0.4)',
                overflow: 'hidden'
              }}
            >
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  bgcolor: 'background.paper',
                  overflowY: 'auto'
                }}
              >
                <Box
                  dangerouslySetInnerHTML={{ __html: renderedHtml }}
                  sx={{
                    width: '100%',
                    maxWidth: '100%'
                  }}
                />
              </Box>
            </Box>
          ) : (
            // Desktop preview
            <Box
              sx={{
                margin: '0 auto',
                bgcolor: 'background.paper',
                boxShadow: theme => theme.palette.mode === 'dark'
                  ? '0 4px 6px rgba(0,0,0,0.3)'
                  : '0 4px 6px rgba(0,0,0,0.1)',
                overflow: 'hidden',
                borderRadius: 2,
                maxWidth: channel === 'Web' ? '1152px' : '640px'
              }}
            >
              <Box
                dangerouslySetInnerHTML={{ __html: renderedHtml }}
                sx={{
                  width: '100%',
                  maxWidth: '100%'
                }}
              />
            </Box>
          )
        ) : !loading && (
          // Show message if no rendered HTML available
          <Box sx={{ padding: 1.5, textAlign: 'center', color: 'text.secondary' }}>
            <Typography variant="body2">No preview available</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

